/* eslint-disable react/prop-types */
import React from 'react';
import { createPortal } from 'react-dom';
import { makeStyles } from '@material-ui/core/styles';
// import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import CloseIcon from '@material-ui/icons/Close';
import { IoSparklesSharp } from 'react-icons/io5';
import { RiChatSmileAiFill } from 'react-icons/ri';

const BRAND_COLOR = '#3E5EA7';
const BRAND_COLOR_HOVER = '#33518f';

const useStyles = makeStyles({
  chatButton: {
    width: 48,
    height: 48,
    padding: 0,
    margin: 0,
    border: 'none',
    borderRadius: '50%',
    backgroundColor: BRAND_COLOR,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 3px 8px rgba(0,0,0,0.3)',
    opacity: 1,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      backgroundColor: BRAND_COLOR_HOVER,
      transform: 'scale(1.08)',
      boxShadow: '0 5px 14px rgba(0,0,0,0.35)',
    },
    '&:active': {
      transform: 'scale(0.95)',
      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
    },
    '&:focus-visible': {
      outline: `2px solid ${BRAND_COLOR}`,
      outlineOffset: 2,
    },
    '& svg': {
      width: 22,
      height: 22,
    },
  },
  dismissBadge: {
    position: 'absolute',
    top: -4,
    left: -4,
    width: 18,
    height: 18,
    minHeight: 18,
    padding: 0,
    borderRadius: '50%',
    backgroundColor: '#555',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    cursor: 'pointer',
    opacity: 1,
    transition: 'transform 0.15s ease, background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#333',
      transform: 'scale(1.15)',
    },
    '&:active': {
      transform: 'scale(0.9)',
    },
    '& svg': {
      width: 12,
      height: 12,
    },
  },
  reopenTab: {
    position: 'fixed',
    bottom: 24,
    right: 0,
    zIndex: 9999,
    width: 24,
    height: 40,
    padding: 0,
    margin: 0,
    border: 'none',
    backgroundColor: BRAND_COLOR,
    color: 'white',
    borderRadius: '8px 0 0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
    opacity: 1,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, background-color 0.2s ease, width 0.2s ease',
    '&:hover': {
      backgroundColor: BRAND_COLOR_HOVER,
      width: 28,
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
  reopenIconWrapper: {
    position: 'relative',
    width: 16,
    height: 16,
  },
  reopenBubble: {
    width: 16,
    height: 16,
  },
  reopenSparkle: {
    position: 'absolute',
    top: -4,
    right: -5,
    width: 9,
    height: 9,
    color: '#FFD54A',
  },
});

// Le bouton principal (rond) n'apparaît que quand le chat est fermé et non masqué.
// Une fois ouvert, la fenêtre Chatbase et sa propre croix native gèrent la fermeture.
// Une fois masqué via le badge, seul l'onglet fin (reopenTab) reste visible.
function ChatToggleButton({
  isOpen, isDismissed, onOpenChat, onDismiss, onUndismiss,
}) {
  const classes = useStyles();

  if (isDismissed) {
    return createPortal(
      <button
        type="button"
        onClick={onUndismiss}
        aria-label="Afficher le chat"
        className={classes.reopenTab}
      >
        <div className={classes.reopenIconWrapper}>
          <IoSparklesSharp className={classes.reopenBubble} />
        </div>
      </button>,
      document.body,
    );
  }

  if (isOpen) return null;

  return createPortal(
    <div style={{
      position: 'fixed', bottom: 16, right: 16, zIndex: 9999,
    }}
    >
      <button
        type="button"
        onClick={onOpenChat}
        aria-label="Ouvrir le chat"
        className={classes.chatButton}
      >
        <RiChatSmileAiFill />
      </button>
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        onKeyDown={(e) => e.key === 'Enter' && onDismiss()}
        aria-label="Masquer le bouton de chat"
        className={classes.dismissBadge}
      >
        <CloseIcon />
      </div>
    </div>,
    document.body,
  );
}

export default ChatToggleButton;
